import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { AccessForSelectFragment } from 'generated/resources/graphql'
import { NotVoid } from 'lodash'

import { observer } from 'mobx-react'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import i18n from 'modules/core/lib/i18n'

import useAddMembersToPermissionGroup from 'modules/permissions/hooks/useAddMembersToPermissionGroup'
import type PermissionGroup from 'modules/permissions/store/models/PermissionGroup'

interface Props {
  onClose: () => NotVoid
  permissionGroup: PermissionGroup
  external: boolean
  excludeIds: number[]
}

const AssignUsersModal = ({ permissionGroup, onClose, excludeIds, external }: Props) => {
  const [selectedAccesses, setSelectedAccesses] = useState<AccessForSelectFragment[]>([])
  const [addMembers, addingMembers] = useAddMembersToPermissionGroup()

  const submitDisabled = selectedAccesses.length === 0 || addingMembers

  const onSelect = (accesses: AccessForSelectFragment[]) => {
    setSelectedAccesses(accesses)
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    await addMembers({
      permissionGroupId: permissionGroup.id,
      accessIds: selectedAccesses.map((access) => access.id),
    })
    closePortal()
  }

  const title = external
    ? i18n.t('settings.permissions.group.actions.assign_users.title')
    : i18n.t('settings.permissions.group.actions.assign_employees.title')
  const description = external
    ? i18n.t('settings.permissions.group.actions.assign_users.description')
    : i18n.t('settings.permissions.group.actions.assign_employees.description')

  return (
    <Modal
      illustration="add-user"
      title={title}
      description={description}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Button submit disabled={submitDisabled}>
          {i18n.t('common.save')}
        </Button>
      }
    >
      <AccessSelector
        width="s400"
        withAvatar
        mandatory
        terminated={false}
        onSelect={onSelect}
        excludeIds={excludeIds}
        external={external}
        multiple
        value={selectedAccesses}
      />
    </Modal>
  )
}

export default observer(AssignUsersModal)
